import { useWorkflowStore } from "@/stores/workflowStore";
import {
  MenuList,
  MenuItem,
  MenuDivider,
  makeStyles,
  tokens,
  Text,
} from "@fluentui/react-components";
import { AddFilled, FlashFilled, SparkleRegular, GridDotsRegular, NoteAddRegular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef } from "react";
import { telemetryService } from "@/services/telemetryService";
import { getShortcutLabel } from "@/utils/input";

const FORMAT_WORKFLOW_SHORTCUT = getShortcutLabel('formatWorkflow');

const useStyles = makeStyles({
  menuContainer: {
    position: "fixed",
    zIndex: 1000,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    minWidth: "240px",
  },
  menuItemContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  menuItemWithDescription: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: tokens.spacingVerticalXXS,
  },
  menuItemLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  menuItemDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  shortcutHint: {
    marginLeft: tokens.spacingHorizontalXL,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    // Use the system UI font (not monospace) so platform-specific
    // shortcut glyphs (⇧ ⌘ ⌥ ⌃ ⏎) render at a consistent size.
    // Most monospace fonts lack these symbols and substitute a tiny
    // fallback that looks misaligned.
    fontFamily: tokens.fontFamilyBase,
    // Subtle keycap chrome to differentiate the shortcut from the label.
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    // Keep glyphs at a consistent baseline height regardless of which
    // characters are present.
    lineHeight: tokens.lineHeightBase200,
  },
});

interface CanvasContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number } | null;
  onClose: () => void;
  onAddTrigger: () => void;
  onAddAction: () => void;
  onAddNote?: () => void;
  onAutoArrange?: () => void;
  onBackToStart?: () => void;
  showBackToStart?: boolean;
  /** When true, menu stays open until user adds something (for empty canvas) */
  sticky?: boolean;
  /** Whether auto-arrange is currently in progress */
  isAutoArranging?: boolean;
}

export const CanvasContextMenu = ({
  isOpen,
  position,
  onClose,
  onAddTrigger,
  onAddAction,
  onAddNote,
  onAutoArrange,
  onBackToStart,
  showBackToStart = false,
  sticky = false,
  isAutoArranging = false,
}: CanvasContextMenuProps) => {
  const styles = useStyles();
  const menuRef = useRef<HTMLDivElement>(null);
  const workflow = useWorkflowStore((s) => s.workflow);

  const hasTriggers = useMemo(
    () =>
      workflow && Object.keys(workflow.definition.triggers || {}).length > 0,
    [workflow]
  );

  const hasActions = useMemo(
    () =>
      workflow && Object.keys(workflow.definition.actions || {}).length > 0,
    [workflow]
  );

  const isEmpty = !hasTriggers && !hasActions;

  // Auto-close sticky menu when canvas becomes non-empty (user added something)
  useEffect(() => {
    if (sticky && !isEmpty && isOpen) {
      onClose();
    }
  }, [sticky, isEmpty, isOpen, onClose]);

  // Close menu when clicking outside (unless sticky and canvas is empty)
  useEffect(() => {
    if (!isOpen) return;
    // Don't add click-outside handler if menu is sticky and canvas is empty
    if (sticky && isEmpty) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, sticky, isEmpty]);

  if (!isOpen || !position) return null;

  return (
    <div
      ref={menuRef}
      className={styles.menuContainer}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <MenuList>
        {!hasTriggers && (
          <>
            <MenuItem
              icon={<FlashFilled />}
              onClick={() => {
                onAddTrigger();
                onClose();
                telemetryService.trackEvent('canvas.context_menu.action_selected', { action: 'add_trigger' });
              }}
            >
              <div className={styles.menuItemWithDescription}>
                <div className={styles.menuItemLabel}>
                  <span>Add Trigger</span>
                </div>
                <Text className={styles.menuItemDescription}>
                  Start your workflow with an event
                </Text>
              </div>
            </MenuItem>
            <MenuDivider />
          </>
        )}
        <MenuItem
          icon={<AddFilled />}
          onClick={() => {
            onAddAction();
            onClose();
            telemetryService.trackEvent('canvas.context_menu.action_selected', { action: 'add_action' });
          }}
        >
          <div className={styles.menuItemWithDescription}>
            <div className={styles.menuItemLabel}>
              <span>Add Action</span>
              <span className={styles.shortcutHint}>Tab</span>
            </div>
            <Text className={styles.menuItemDescription}>
              Add a step to your workflow
            </Text>
          </div>
        </MenuItem>
        {onAddNote && (
          <MenuItem
            icon={<NoteAddRegular />}
            onClick={() => {
              onAddNote();
              onClose();
              telemetryService.trackEvent('canvas.context_menu.action_selected', { action: 'add_note' });
            }}
          >
            <div className={styles.menuItemWithDescription}>
              <div className={styles.menuItemLabel}>
                <span>Add Note</span>
              </div>
              <Text className={styles.menuItemDescription}>
                Add a sticky note to the canvas
              </Text>
            </div>
          </MenuItem>
        )}
        {!isEmpty && onAutoArrange && (
          <>
            <MenuDivider />
            <MenuItem
              icon={<GridDotsRegular />}
              onClick={() => {
                onAutoArrange();
                onClose();
                telemetryService.trackEvent('canvas.context_menu.action_selected', { action: 'format_workflow' });
              }}
              disabled={isAutoArranging}
            >
              <div className={styles.menuItemWithDescription}>
                <div className={styles.menuItemLabel}>
                  <span>{isAutoArranging ? 'Formatting...' : 'Format Workflow'}</span>
                  <span className={styles.shortcutHint}>{FORMAT_WORKFLOW_SHORTCUT}</span>
                </div>
                <Text className={styles.menuItemDescription}>
                  Automatically layout all nodes
                </Text>
              </div>
            </MenuItem>
          </>
        )}
        {showBackToStart && isEmpty && onBackToStart && (
          <>
            <MenuDivider />
            <MenuItem
              icon={<SparkleRegular />}
              onClick={() => {
                onBackToStart();
                onClose();
                telemetryService.trackEvent('canvas.context_menu.action_selected', { action: 'use_ai_assistant' });
              }}
            >
              <div className={styles.menuItemWithDescription}>
                <div className={styles.menuItemLabel}>
                  <span>Use Copilot</span>
                </div>
                <Text className={styles.menuItemDescription}>
                  Describe what you want to build
                </Text>
              </div>
            </MenuItem>
          </>
        )}
      </MenuList>
    </div>
  );
};
